import { View } from 'react-native';
import { Icon } from 'shared/components/icon';
import { fasVideoCamera } from 'shared/@generated/icons/fasVideoCamera';
import { fasPhoneHangup } from 'shared/@generated/icons/fasPhoneHangup';
import { fasSearch } from 'shared/@generated/icons/fasSearch';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import { TextLine } from './illustration-interface';
import { Avatar, Caption, Lines, Photo, useIllustrationCopy } from './illustration-parts';

export function ContactHeader({
  s,
  width,
  person = 'seller',
}: {
  s: IllustrationScale;
  width: number;
  person?: string;
}) {
  const t = useIllustrationCopy();
  return (
    <View className="flex-row items-center" style={{ width: s(width), gap: s(4) }}>
      <Avatar s={s} size={12} />
      <View className="flex-1" style={{ gap: s(2) }}>
        <Caption s={s} size={5.5}>
          {t(person)}
        </Caption>
        <TextLine s={s} width={width * 0.45} height={1.5} className="bg-brand-brown-dark/20" />
      </View>
    </View>
  );
}

export function MessageBubble({
  s,
  width,
  outgoing = false,
  rows = 2,
}: {
  s: IllustrationScale;
  width: number;
  outgoing?: boolean;
  rows?: number;
}) {
  return (
    <View
      style={{
        width: s(width),
        padding: s(4),
        borderRadius: s(5),
        borderBottomLeftRadius: s(outgoing ? 5 : 1),
        borderBottomRightRadius: s(outgoing ? 1 : 5),
        backgroundColor: outgoing ? palette.sage : palette.creamDark,
        alignSelf: outgoing ? 'flex-end' : 'flex-start',
      }}
    >
      <Lines s={s} width={width - 8} rows={rows} />
    </View>
  );
}

export function SharedConversationPhoto({
  s,
  width,
  height,
}: {
  s: IllustrationScale;
  width: number;
  height: number;
}) {
  return (
    <View testID="conversation-shared-photo">
      <Photo s={s} width={width} height={height} tone="clay" />
    </View>
  );
}

export function MessageThread({
  s,
  width = 42,
  facing = false,
}: {
  s: IllustrationScale;
  width?: number;
  facing?: boolean;
}) {
  return (
    <View style={{ width: s(width), gap: s(4) }}>
      <ContactHeader s={s} width={width} person={facing ? 'seller' : 'sample_other_seller'} />
      <MessageBubble s={s} width={width * 0.8} outgoing={facing} />
      <MessageBubble s={s} width={width * 0.71} outgoing={!facing} rows={1} />
      <MessageBubble s={s} width={width * 0.87} outgoing={facing} rows={1} />
      <View style={{ alignSelf: facing ? 'flex-start' : 'flex-end' }}>
        <SharedConversationPhoto s={s} width={width * 0.81} height={width * 0.58} />
      </View>
    </View>
  );
}

export function InboxList({ s, width = 92 }: { s: IllustrationScale; width?: number }) {
  const t = useIllustrationCopy();
  return (
    <View style={{ width: s(width) }}>
      <Caption s={s} size={9.5}>
        {t('inbox')}
      </Caption>
      <View
        className="bg-brand-cream-dark justify-center"
        style={{ height: s(10), borderRadius: s(4), paddingHorizontal: s(5), marginTop: s(4) }}
      >
        <View testID="inbox-search-icon">
          <Icon
            icon={fasSearch}
            className="text-brand-brown-dark/60"
            style={{ width: s(5.5), height: s(5.5) }}
          />
        </View>
      </View>
      <View style={{ marginTop: s(4) }}>
        {(
          ['seller', 'sample_other_seller', 'sample_third_seller', 'sample_fourth_seller'] as const
        ).map((person, index) => (
          <View
            testID={'inbox-row-' + index}
            key={person}
            className="flex-row items-center"
            style={{ width: s(width), height: s(22.5), gap: s(6) }}
          >
            <Avatar s={s} size={16} />
            <View
              testID={'inbox-row-content-' + index}
              className="flex-1 justify-center"
              style={{ height: s(18), gap: s(2) }}
            >
              <Caption s={s} size={7}>
                {t(person)}
              </Caption>
              <View style={{ gap: s(1.5) }}>
                <TextLine
                  s={s}
                  width={width - 32}
                  height={1.5}
                  className="bg-brand-brown-dark/25"
                />
                <TextLine
                  s={s}
                  width={(width - 32) * (index % 2 ? 0.65 : 0.8)}
                  height={1.5}
                  className="bg-brand-brown-dark/15"
                />
              </View>
            </View>
            <View className="items-center justify-center" style={{ width: s(3), height: s(18) }}>
              {index < 2 && (
                <View
                  testID={'inbox-unread-' + index}
                  className="rounded-full bg-red-600"
                  style={{ width: s(3), height: s(3) }}
                />
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

export function CallToolbar({ s, size = 12 }: { s: IllustrationScale; size?: number }) {
  return (
    <View className="flex-row items-center justify-center" style={{ gap: s(7) }}>
      {[fasVideoCamera, fasPhoneHangup].map((icon, index) => (
        <View
          key={icon.name}
          className="items-center justify-center rounded-full"
          style={{
            width: s(size),
            height: s(size),
            backgroundColor: index ? palette.clay : palette.forest,
          }}
        >
          <Icon
            icon={icon}
            className="text-brand-cream-light"
            style={{ width: s(size * 0.43), height: s(size * 0.43) }}
          />
        </View>
      ))}
    </View>
  );
}

export function CallPortrait({
  s,
  width,
  height,
  person = 'seller',
}: {
  s: IllustrationScale;
  width: number;
  height: number;
  person?: string;
}) {
  const t = useIllustrationCopy();
  return (
    <View
      className="items-center justify-center"
      style={{
        width: s(width),
        height: s(height),
        borderRadius: s(5),
        backgroundColor: palette.sage,
        gap: s(5),
      }}
    >
      <Avatar s={s} size={Math.min(width * 0.54, height * 0.58)} />
      <Caption s={s} size={5.8}>
        {t(person)}
      </Caption>
    </View>
  );
}

export function PhoneCall({ s }: { s: IllustrationScale }) {
  const t = useIllustrationCopy();
  return (
    <View className="items-center" style={{ width: s(54), gap: s(8) }}>
      <Caption s={s} size={6.5}>
        {t('video')}
      </Caption>
      <CallPortrait s={s} width={54} height={72} />
      <TextLine s={s} width={15} height={1.5} className="bg-brand-brown-dark/20" />
      <CallToolbar s={s} size={13} />
    </View>
  );
}
